/* eslint-disable */
import React, { useCallback } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import CodeIcon from "@mui/icons-material/Code";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LinkIcon from "@mui/icons-material/Link";
import { toast } from "react-toastify";

import { GENERATE_SCHEMA_MARKUP } from "../../gql/queries/ai";

// --------------- Styled Components ---------------

const CodeBlock = styled("pre")(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[100],
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  overflowX: "auto",
  fontFamily: "monospace",
  fontSize: "0.8125rem",
  lineHeight: 1.6,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: 400,
  border: `1px solid ${theme.palette.divider}`,
}));

const CodeWrapper = styled(Box)(() => ({
  position: "relative",
}));

const CopyBtn = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[200],
  "&:hover": {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[300],
  },
}));

const PlaceholderBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(6),
  textAlign: "center",
  color: theme.palette.text.secondary,
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}));

// --------------- Helpers ---------------

const copyToClipboard = text => {
  navigator.clipboard.writeText(text).then(
    () => toast.success("Copied to clipboard"),
    () => toast.error("Failed to copy"),
  );
};

const formatJsonLd = jsonLd => {
  if (!jsonLd) return "";
  if (typeof jsonLd === "string") {
    try {
      return JSON.stringify(JSON.parse(jsonLd), null, 2);
    } catch {
      return jsonLd;
    }
  }
  return JSON.stringify(jsonLd, null, 2);
};

const parseList = val => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try {
    return JSON.parse(val);
  } catch {
    return [val];
  }
};

// --------------- Component ---------------

const SchemaGeneratorModal = ({ open, onClose, sessionId, url }) => {
  // ---- Query ----
  const [fetchSchema, { data: schemaData, loading: schemaLoading }] = useLazyQuery(GENERATE_SCHEMA_MARKUP, { fetchPolicy: "network-only" });

  // ---- Handlers ----
  const handleGenerate = useCallback(() => {
    if (!sessionId || !url) {
      toast.warning("Session ID and URL are required");
      return;
    }
    fetchSchema({ variables: { sessionId, url } });
  }, [fetchSchema, sessionId, url]);

  const handleCopyJsonLd = useCallback(() => {
    if (schemaResult?.jsonLd) {
      const formatted = formatJsonLd(schemaResult.jsonLd);
      // Copy with script tag wrapper
      const fullSnippet = `<script type="application/ld+json">\n${formatted}\n</script>`;
      copyToClipboard(fullSnippet);
    }
  }, [schemaData]);

  // ---- Derived data ----
  const schemaResult = schemaData?.generateSchemaMarkup || null;
  const recommendations = parseList(schemaResult?.recommendations);
  const formattedJson = formatJsonLd(schemaResult?.jsonLd);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: 400 } }}>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CodeIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Schema.org Generator
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {/* URL Display */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            bgcolor: t => (t.palette.mode === "dark" ? "grey.900" : "grey.50"),
            border: t => `1px solid ${t.palette.divider}`,
          }}
        >
          <LinkIcon fontSize="small" color="action" />
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.8125rem",
              wordBreak: "break-all",
              flex: 1,
            }}
          >
            {url || "No URL provided"}
          </Typography>
        </Box>

        {/* Generate Button */}
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={schemaLoading || !sessionId || !url}
          startIcon={schemaLoading ? <CircularProgress size={16} color="inherit" /> : <CodeIcon />}
          sx={{ mb: 3 }}
        >
          {schemaLoading ? "Generating Schema..." : "Generate Schema"}
        </Button>

        {/* Placeholder */}
        {!schemaResult && !schemaLoading && (
          <PlaceholderBox>
            <CodeIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
              No Schema Generated
            </Typography>
            <Typography variant="body2">Click "Generate Schema" to create structured data markup for this page.</Typography>
          </PlaceholderBox>
        )}

        {/* Loading */}
        {schemaLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Results */}
        {schemaResult && (
          <>
            {/* Schema Type */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Schema Type:
              </Typography>
              <Chip label={schemaResult.schemaType || "Unknown"} color="primary" variant="outlined" />
            </Box>

            {/* JSON-LD Code Block */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                JSON-LD Markup:
              </Typography>
              <CodeWrapper>
                <CodeBlock>
                  <code>{`<script type="application/ld+json">\n${formattedJson}\n</script>`}</code>
                </CodeBlock>
                <CopyBtn size="small" onClick={handleCopyJsonLd}>
                  <ContentCopyIcon fontSize="small" />
                </CopyBtn>
              </CodeWrapper>
            </Box>

            {/* Recommendations */}
            {recommendations.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Recommendations:
                </Typography>
                <List dense>
                  {recommendations.map((rec, idx) => (
                    <ListItem key={idx} disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleOutlineIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={rec} primaryTypographyProps={{ variant: "body2" }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SchemaGeneratorModal;
